import { FC } from 'react';

import { VisuallyHidden } from '@purpur/library';
import { match, P } from 'ts-pattern';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { ProductSearchItem } from '@telialt/core-search/src/model';
import { withParams } from '@telialt/utils/src/helpers';
import { TeliaSegment, useSegment } from '@telialt/utils/src/segment';

import {
    CurrencyText,
    CurrencyTextBold,
    FullPriceBlock,
    GrayText,
    LeasingInitialAmountBlock,
    MonthlyPriceBlock,
    MonthlyPriceLabelText,
    OldPrice,
    PriceBlock,
    PriceText,
} from './CompareProductCard.styles';

type Props = {
    product: ProductSearchItem;
};

export const ProductPricings: FC<Props> = ({ product }) => {
    const { t } = useTranslations();

    const segment = useSegment();

    const monthlyPrice = product.monthlyPriceFormatted;
    const fullPrice = product.fullPriceFormatted;
    const oldFullPrice = product.oldFullPrice;
    const leasingInitialAmount = segment === TeliaSegment.B2b ? product.leasingInitialAmountFormatted : undefined;
    const residualValue = product.residualValueFormatted;

    const leasingCommitment = product.leasingCommitment;
    const commitment = match({ segment, leasingCommitment })
        .with({ segment: TeliaSegment.B2b }, () => product.commitment)
        .with({ leasingCommitment: P.not(P.nullish) }, () =>
            withParams(t['search.component.product_card.leasing_commitment'], { leasingCommitment }),
        )
        .otherwise(() => undefined);

    if (!monthlyPrice && !fullPrice) {
        return null;
    }

    return (
        <div>
            <PriceBlock>
                {(monthlyPrice || leasingInitialAmount) && (
                    <div>
                        {monthlyPrice && (
                            <MonthlyPriceBlock>
                                <div>
                                    <MonthlyPriceLabelText>
                                        {t['search.component.product_card.monthly_price_label']}
                                    </MonthlyPriceLabelText>
                                    <PriceText>{monthlyPrice}</PriceText>
                                    <CurrencyText>
                                        <span aria-hidden="true">
                                            {t['search.component.product_card.monthly_price_unit']}
                                        </span>
                                        <VisuallyHidden>
                                            {t['search.component.product_card.monthly_price_unit_accessible']}
                                        </VisuallyHidden>
                                    </CurrencyText>
                                </div>
                                {commitment && (
                                    <div>
                                        <GrayText>{commitment}</GrayText>
                                    </div>
                                )}
                                {!!residualValue && (
                                    <GrayText>
                                        {withParams(t['search.component.product_card.residual_value'], {
                                            price: residualValue,
                                        })}
                                    </GrayText>
                                )}
                            </MonthlyPriceBlock>
                        )}
                        {leasingInitialAmount && (
                            <LeasingInitialAmountBlock>
                                <GrayText>
                                    {withParams(t['search.component.product_card.leasing_initial_amount'], {
                                        price: leasingInitialAmount,
                                    })}
                                </GrayText>
                            </LeasingInitialAmountBlock>
                        )}
                    </div>
                )}
                {fullPrice && (
                    <FullPriceBlock $hasMonthlyPrice={!!monthlyPrice}>
                        <div>
                            <PriceText>{fullPrice}</PriceText>
                            <CurrencyTextBold>{t['search.component.product_card.full_price_unit']}</CurrencyTextBold>
                        </div>
                        {oldFullPrice && (
                            <OldPrice aria-hidden="true">
                                {withParams(t['search.component.product_card.old_full_price'], {
                                    price: oldFullPrice,
                                })}
                            </OldPrice>
                        )}
                    </FullPriceBlock>
                )}
            </PriceBlock>
        </div>
    );
};
